import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './SectionRentalRequirements.module.css';

const { Money } = sdkTypes;

const ensureMoney = moneyObj => {
  if (!moneyObj) return null;
  if (moneyObj instanceof Money) return moneyObj;
  return new Money(moneyObj.amount, moneyObj.currency);
};

const SectionRentalRequirements = props => {
  const { publicData, marketplaceCurrency, intl } = props;
  const hasSecurityDeposit =
    publicData?.deposit_or_insurance?.includes('security_deposit') ||
    publicData?.deposit_or_insurance?.includes('security-deposit') ||
    publicData?.deposit_or_insurance?.includes('securityDeposit') ||
    publicData?.renterRequirements?.includes('security-deposit') ||
    publicData?.renterRequirements?.includes('security_deposit') ||
    publicData?.renterRequirements?.includes('securityDeposit') ||
    publicData?.rentalRequirements?.includes('security-deposit') ||
    publicData?.rentalRequirements?.includes('security_deposit') ||
    publicData?.rentalRequirements?.includes('securityDeposit') ||
    publicData?.securityDepositEnabled === true;

  const rawDepositAmount =
    publicData?.security_deposit ||
    publicData?.securityDeposit ||
    publicData?.securityDepositAmount ||
    publicData?.customSecurityDeposit;

  const hasGripProtection =
    publicData?.gripProtectionEnabled === true ||
    publicData?.renterRequirements?.includes('grip-protection') ||
    publicData?.renterRequirements?.includes('grip_protection') ||
    publicData?.renterRequirements?.includes('gripProtection') ||
    publicData?.renterRequirements?.includes('gripMarketplaceProtection') ||
    publicData?.rentalRequirements?.includes('grip-protection') ||
    publicData?.rentalRequirements?.includes('grip_protection') ||
    publicData?.rentalRequirements?.includes('gripProtection') ||
    publicData?.rentalRequirements?.includes('gripMarketplaceProtection');

  const rawGripTier =
    publicData?.gripProtectionTier ||
    publicData?.gripMarketplaceProtection ||
    publicData?.gripProtection ||
    publicData?.protectionTier;

  const coiRequired =
    publicData?.coiRequired === true ||
    publicData?.deposit_or_insurance?.includes('insurance_certificate') ||
    publicData?.deposit_or_insurance?.includes('insurance-certificate') ||
    publicData?.renterRequirements?.includes('insurance-certificate') ||
    publicData?.renterRequirements?.includes('insurance_certificate') ||
    publicData?.renterRequirements?.includes('insuranceCertificate') ||
    publicData?.renterRequirements?.includes('coi') ||
    publicData?.rentalRequirements?.includes('insurance-certificate') ||
    publicData?.rentalRequirements?.includes('insurance_certificate') ||
    publicData?.rentalRequirements?.includes('insuranceCertificate');

  if (!hasSecurityDeposit && !hasGripProtection && !coiRequired) {
    return null;
  }

  const depositAmountSubunits = rawDepositAmount != null
    ? (rawDepositAmount > 10000 ? rawDepositAmount : rawDepositAmount * 100)
    : null;

  const depositAmountMoney = hasSecurityDeposit && depositAmountSubunits != null
    ? new Money(depositAmountSubunits, marketplaceCurrency)
    : null;

  const formattedDeposit = depositAmountMoney
    ? formatMoney(intl, depositAmountMoney)
    : null;

  const displayGripTier = () => {
    if (!rawGripTier) return '';
    const lowerTier = String(rawGripTier).toLowerCase();
    const translationKey = `ListingPage.${lowerTier}`;
    if (intl.messages[translationKey]) {
      return intl.formatMessage({ id: translationKey });
    }
    return String(rawGripTier)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Render inline SVGs for professional icons
  const depositIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );

  const shieldIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );

  const coiIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );

  return (
    <section id="rental-requirements" className={css.sectionRequirements}>
      <h2 className={css.title}>
        <FormattedMessage id="ListingPage.rentalRequirementsTitle" />
      </h2>
      <div className={css.requirementsList}>
        {hasSecurityDeposit && formattedDeposit && (
          <div className={css.requirementItem}>
            <div className={css.iconWrapper}>{depositIcon}</div>
            <div className={css.itemTextContainer}>
              <span className={css.itemLabel}>
                <FormattedMessage id="ListingPage.securityDeposit" />
              </span>
              <span className={css.itemDetail}>
                <FormattedMessage id="ListingPage.securityDepositRequired" values={{ amount: formattedDeposit }} />
              </span>
            </div>
          </div>
        )}

        {hasGripProtection && rawGripTier && (
          <div className={css.requirementItem}>
            <div className={css.iconWrapper}>{shieldIcon}</div>
            <div className={css.itemTextContainer}>
              <span className={css.itemLabel}>
                <FormattedMessage id="ListingPage.gripProtection" />
              </span>
              <span className={css.itemDetail}>
                <FormattedMessage
                  id="ListingPage.gripProtectionTier"
                  values={{
                    tier: displayGripTier(),
                  }}
                />
              </span>
            </div>
          </div>
        )}

        {coiRequired && (
          <div className={css.requirementItem}>
            <div className={css.iconWrapper}>{coiIcon}</div>
            <div className={css.itemTextContainer}>
              <span className={css.itemLabel}>
                <FormattedMessage id="ListingPage.coiRequired" />
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SectionRentalRequirements;
